/**
 * Détection et libellés des « modèles système » CMS avancé
 * (en-tête, pied de page, parcours, fiche rubrique…) — distincts des
 * pages éditoriales et du `sourceTemplateKey` (starter à la création).
 */
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import type { PuckSystemModelMeta } from "@/lib/cms-puck/types";
import type { PuckPageListItem } from "@/api/cms-puck/types";

export type SystemModelPageLike = PuckSystemModelMeta & {
  /** Starter key — fallback si le flag API n'est pas encore présent. */
  sourceTemplateKey?: string | null;
};

const SELECTION_CATEGORY_DETAIL_TEMPLATE_KEY = "selection-category-detail";

export function isSystemModelPage(page: SystemModelPageLike | null | undefined): boolean {
  if (!page) return false;
  if (page.isMaintenancePage || page.isAbonnementPage) return true;
  if (page.isSelectionCategoryDetailPage) return true;
  if (page.sourceTemplateKey === SELECTION_CATEGORY_DETAIL_TEMPLATE_KEY) {
    return true;
  }
  if ((page.shellFlowRoles?.length ?? 0) > 0) return true;
  if ((page.catalogFlowRoles?.length ?? 0) > 0) return true;
  if ((page.memberFlowRoles?.length ?? 0) > 0) return true;
  if ((page.searchFlowRoles?.length ?? 0) > 0) return true;
  return false;
}

/** Libellé métier court du rôle principal (pour chip éditeur). */
export function systemModelKindLabel(
  page: SystemModelPageLike | null | undefined,
): string {
  if (!page) return cmsNew("cms.new.systemModel.generic", "Modèle système");

  if (
    page.isSelectionCategoryDetailPage ||
    page.sourceTemplateKey === SELECTION_CATEGORY_DETAIL_TEMPLATE_KEY
  ) {
    return cmsNew(
      "cms.new.systemModel.selectionCategoryDetail",
      "Fiche rubrique",
    );
  }
  if (page.isMaintenancePage) {
    return cmsNew("cms.new.auto.label.maintenance", "Maintenance");
  }
  if (page.isAbonnementPage) {
    return cmsNew("cms.new.pages.abonnementBadge", "Abonnement");
  }

  const shell = page.shellFlowRoles?.[0];
  if (shell === "HEADER") {
    return cmsNew("cms.new.auto.label.en_tete", "En-tête");
  }
  if (shell === "FOOTER") {
    return cmsNew("cms.new.auto.label.pied_de_page", "Pied de page");
  }

  const catalog = page.catalogFlowRoles?.[0];
  if (catalog) {
    const target =
      catalog.target === "ARTWORKS"
        ? cmsNew("cms.new.catalogue.badgeArtworks", "œuvres")
        : cmsNew("cms.new.catalogue.badgeMultiples", "multiples");
    const step =
      catalog.step === "LISTING"
        ? cmsNew("cms.new.catalogue.stepListing", "Listing")
        : catalog.step === "DETAIL"
          ? cmsNew("cms.new.catalogue.stepDetail", "Fiche")
          : cmsNew("cms.new.catalogue.stepPresentation", "Découpage");
    return `${step} ${target}`;
  }

  const member = page.memberFlowRoles?.[0];
  if (member === "LOGIN") {
    return cmsNew("cms.new.blocks.memberLogin.label", "Connexion");
  }
  if (member === "SIGNUP") {
    return cmsNew("cms.new.blocks.memberSignup.label", "Inscription");
  }
  if (member === "PROFILE") {
    return cmsNew("cms.new.blocks.memberProfile.label", "Profil");
  }
  if (member === "FAVORITES") {
    return cmsNew("cms.new.blocks.memberFavorites.label", "Favoris");
  }

  const search = page.searchFlowRoles?.[0];
  if (search) {
    const target =
      search.target === "ARTWORKS"
        ? cmsNew("cms.new.catalogue.badgeArtworks", "œuvres")
        : cmsNew("cms.new.catalogue.badgeMultiples", "multiples");
    return `${cmsNew("cms.new.systemModel.searchResults", "Recherche")} ${target}`;
  }

  return cmsNew("cms.new.systemModel.generic", "Modèle système");
}

/** Narrow helper for list items (même shape). */
export function isSystemModelListItem(page: PuckPageListItem): boolean {
  return isSystemModelPage(page);
}
